import {
  ArrowsClockwiseIcon,
  BookOpenIcon,
  HandWavingIcon,
  HeadsetIcon,
  InfoIcon,
  MegaphoneIcon,
  UserPlusIcon,
} from "@phosphor-icons/react";
import { useTranslate } from "@refinedev/core";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { popIn, slideReveal } from "./Autopilot.styled";
import { radius } from "@libertify/ui";

export const ObjectiveCardsVisual: React.FC<{
  isActive: boolean;
  isDone: boolean;
  objectives: string[] | null;
}> = ({ isActive, isDone, objectives }) => {
  const t = useTranslate();

  const OBJECTIVE_CARD_DEFS = [
    {
      value: "Support",
      label: t("media.autopilot.objectives.support"),
      Icon: HeadsetIcon,
    },
    {
      value: "Persuade",
      label: t("media.autopilot.objectives.persuade"),
      Icon: MegaphoneIcon,
    },
    {
      value: "Onboard",
      label: t("media.autopilot.objectives.onboard"),
      Icon: UserPlusIcon,
    },
    {
      value: "Inform",
      label: t("media.autopilot.objectives.inform"),
      Icon: InfoIcon,
    },
    {
      value: "Learn",
      label: t("media.autopilot.objectives.learn"),
      Icon: BookOpenIcon,
    },
    {
      value: "Engage",
      label: t("media.autopilot.objectives.engage"),
      Icon: HandWavingIcon,
    },
    {
      value: "Retain",
      label: t("media.autopilot.objectives.retain"),
      Icon: ArrowsClockwiseIcon,
    },
  ];

  const objIdxs = (objectives ?? [])
    .map((obj) => OBJECTIVE_CARD_DEFS.findIndex((d) => d.value === obj))
    .filter((i) => i !== -1);

  const [visibleCount, setVisibleCount] = useState(0);
  const [selectedIdxs, setSelectedIdxs] = useState<number[]>([]);
  const objIdxsRef = useRef(objIdxs);
  useEffect(() => {
    objIdxsRef.current = objIdxs;
  });

  // Done steps derive state from data — handles late-arriving objectives
  const doneVisibleCount = isDone ? OBJECTIVE_CARD_DEFS.length : visibleCount;
  const doneSelectedIdxs = isDone ? objIdxs : selectedIdxs;

  useEffect(() => {
    if (!isActive) return;
    const timers: ReturnType<typeof setTimeout>[] = [];
    OBJECTIVE_CARD_DEFS.forEach((_, i) => {
      timers.push(
        setTimeout(() => setVisibleCount((c) => c + 1), i * 180 + 100)
      );
    });
    const allDone = OBJECTIVE_CARD_DEFS.length * 180 + 400;
    timers.push(
      setTimeout(() => setSelectedIdxs(objIdxsRef.current.slice(0, 1)), allDone)
    );
    timers.push(
      setTimeout(() => setSelectedIdxs(objIdxsRef.current), allDone + 500)
    );
    return () => timers.forEach(clearTimeout);
  }, [isActive]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ObjCardGrid>
      {OBJECTIVE_CARD_DEFS.map(({ value, label, Icon }, i) => {
        if (i >= doneVisibleCount) return null;
        const isSelected = doneSelectedIdxs.includes(i);
        const isDimmed = doneSelectedIdxs.length > 0 && !isSelected;
        return (
          <ObjCard
            key={value}
            $selected={isSelected}
            $dimmed={isDimmed}
            $delay={0}
          >
            <Icon
              size={22}
              weight="duotone"
              color={isSelected ? "#14B8A6" : "#9ca3af"}
            />
            <ObjCardLabel $selected={isSelected}>{label}</ObjCardLabel>
          </ObjCard>
        );
      })}
    </ObjCardGrid>
  );
};

const ObjCardGrid = styled.div`
  padding: 14px 15px 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  animation: ${slideReveal} 0.3s ease both;
`;

const ObjCard = styled.div<{
  $selected: boolean;
  $dimmed: boolean;
  $delay: number;
}>`
  width: 80px;
  height: 88px;
  border-radius: ${radius.box}px;
  border: 1.5px solid ${(p) => (p.$selected ? "#14B8A6" : "#E5E7EB")};
  background: ${(p) => (p.$selected ? "#F0FDFA" : "white")};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 7px;
  cursor: default;
  opacity: ${(p) => (p.$dimmed ? 0.4 : 1)};
  transform: ${(p) =>
    p.$selected ? "scale(1.04)" : p.$dimmed ? "scale(0.96)" : "scale(1)"};
  transition:
    border-color 0.35s ease,
    background 0.35s ease,
    opacity 0.35s ease,
    transform 0.35s ease;
  animation: ${popIn} 0.3s ease ${(p) => p.$delay}ms both;
`;

const ObjCardLabel = styled.span<{ $selected: boolean }>`
  font-size: 11px;
  font-weight: 600;
  color: ${(p) => (p.$selected ? "#0d9488" : "#6b7280")};
  transition: color 0.35s ease;
`;
